// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngLoadingOverlay from "@ces/App/Components/Common/XngLoadingOverlay";
import { getNotifyStyle } from "@ces/App/Components/Notify";
import TitleTypography, { TitleTypographyStyles } from "@ces/App/Components/TitleTypography/TitleTypography";
import dataTableSharedStyles from "@ces/App/SharedStyles/MyAccountPages/List/DataTableStyles";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@ces/Overrides/Components/OrderSummaryCard";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    // XNG-Changes
    showOrderNumber = "showOrderNumber",
    showDate = "showDate",
    showOrderTotal = "showOrderTotal",
    showStatus = "showStatus",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showReorderProducts = "showReorderProducts",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.showOrderNumber]: boolean;
        [fields.showDate]: boolean;
        [fields.showOrderTotal]: boolean;
        [fields.showStatus]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showReorderProducts]: boolean;
    };
}

// XNG-Changes
let lastOrdersDataView: { value?: OrderModel[]; isLoading: boolean } | null = null;

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;

    // XNG-Changes
    const ordersDataView = getOrdersDataView(state, recentOrdersParameter);
    if (!ordersDataView.isLoading || !lastOrdersDataView) {
        lastOrdersDataView = ordersDataView;
    }

    return {
        ordersDataView: lastOrdersDataView,
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        // XNG-Changes
        isLoading: ordersDataView.isLoading,
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    // XNG-Changes
    titleTypographyStyles?: TitleTypographyStyles;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    orderTableHeaders?: DataTableHeaderProps;
    dateTableHeaders?: DataTableHeaderProps;
    poTableHeaders?: DataTableHeaderProps;
    totalTableHeaders?: DataTableHeaderProps;
    shipToTableHeaders?: DataTableHeaderProps;
    actionsTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
};

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-grid-item{
}

.description-grid-item{
}

.no-orders-found-text{
}

.card_container__wrapper{
}

.order-summary-card-grid-item{
}

.order-summary-card-container{
}

.page-type-link-grid-item{
}

.order-number-grid-item{
}

.status-grid-item{
}

.web-order-number-grid-item{
}

.total-grid-item{
}`;

// XNG-Change: mapping new values using for sorting
type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    invoiceNumber: ["invoiceNumber"],
    enteredDate: ["enteredDate", "invoiceNumber"],
    orderTotal: ["orderTotal"],
    status: ["status"],
    stCompanyName: ["stCompanyName"],
    customerPoNum: ["customerPoNum"],
};

let recentOrdersParameter: GetOrdersApiParameter = {
    page: 1,
    // Xng-Change
    sort: "enteredDate DESC, invoiceNumber DESC",
    customerSequence: "-1",
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    headerClick(sortField: string) {
        const previousSort = recentOrdersParameter.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const sort = sortByFields[sortField]?.map(field => `${field}${direction}`).join(", ");
        recentOrdersParameter.sort = sort;
        this.props.loadOrders({ ...recentOrdersParameter });
    }

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (!recentOrdersParameter.sort) {
            return sorted;
        }
        const [primaryField] = recentOrdersParameter.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders, isLoading } = this.props;

        // XNG-Changes: don't show widgte if there are no recent orders
        if (!canViewOrders || !ordersDataView.value || ordersDataView.value.length === 0) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        // XNG-Changes
        const cmsProperties = {
            showOrderNumber: this.props.fields.showOrderNumber,
            showDate: this.props.fields.showDate,
            showOrderTotal: this.props.fields.showOrderTotal,
            showStatus: this.props.fields.showStatus,
            showShipTo: this.props.fields.showShipTo,
            showPONumber: this.props.fields.showPONumber,
            showReorderProducts: this.props.fields.showReorderProducts,
        };

        return (
            // XNG-Changes : Add loading overlay
            <XngLoadingOverlay id="recent-order-widget" loading={isLoading}>
                <StyledWrapper {...customCssWrapper}>
                    <TitleTypography
                        extendedStyles={styles.titleTypographyStyles}
                        linkText={translate("View All Recent Orders")}
                        link={orderHistoryUrl}
                        showIcon={true}
                    >
                        {translate("Recent Orders")}
                    </TitleTypography>

                    {/* {ordersDataView.value.length === 0 && (
                    <GridItem {...styles.noOrdersFoundGridItem}>
                        <Typography {...styles.noOrdersFoundText}>{translate("No orders found")}</Typography>
                    </GridItem>
                )} */}
                    {ordersDataView.value.length > 0 && (
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                {cmsProperties.showOrderNumber && (
                                    <DataTableHeader
                                        {...styles.orderTableHeaders}
                                        tight
                                        sorted={this.sorted("invoiceNumber")}
                                        onSortClick={() => this.headerClick("invoiceNumber")}
                                    >
                                        {translate("Order #")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showDate && (
                                    <DataTableHeader
                                        {...styles.dateTableHeaders}
                                        tight
                                        onSortClick={() => this.headerClick("enteredDate")}
                                        aria-label="none"
                                        data-test-selector="tableHeader_OrderDate"
                                    >
                                        {translate(" Order Date")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showShipTo && (
                                    <DataTableHeader {...styles.shipToTableHeaders}>
                                        {translate("Ship To/Pick Up")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showPONumber && (
                                    <DataTableHeader
                                        {...styles.poTableHeaders}
                                        tight
                                        sorted={this.sorted("customerPoNum")}
                                        onSortClick={() => this.headerClick("customerPoNum")}
                                    >
                                        {translate("Purchase Order #")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showOrderTotal && (
                                    <DataTableHeader
                                        {...styles.totalTableHeaders}
                                        tight
                                        sorted={this.sorted("orderTotal")}
                                        onSortClick={() => this.headerClick("orderTotal")}
                                    >
                                        {translate("Order Total")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showStatus && (
                                    <DataTableHeader
                                        {...styles.dataTableHeaders}
                                        tight
                                        onSortClick={() => this.headerClick("status")}
                                        data-test-selector="tableHeader_status"
                                    >
                                        {translate("Status")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showReorderProducts && (
                                    <DataTableHeader {...styles.actionsTableHeaders} tight>
                                        {translate("Actions")}
                                    </DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {ordersDataView.value.map(
                                    order =>
                                        order !== null && (
                                            <OrderSummaryCard
                                                key={order.id}
                                                order={order}
                                                orderSettings={settingsCollection.orderSettings}
                                                cmsProperties={cmsProperties}
                                                extendedStyles={styles.orderSummaryCard}
                                            />
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    )}
                </StyledWrapper>
            </XngLoadingOverlay>
        );
    }
}

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes
            {
                name: fields.showOrderNumber,
                displayName: "Order #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showDate,
                displayName: "Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showOrderTotal,
                displayName: "Order Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPONumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showReorderProducts,
                displayName: "Reorder",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
        ],
    },
};

export default widgetModule;
